import React, { useCallback, useEffect, useState } from 'react';

import { useSelector } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';
import { iStore } from '~/domain/interfaces/models';

import {
  Adm,
  AppointmentHistory,
  ClinicalDocs,
  Container,
  Drop,
  Duty,
  Image,
  LeftItem,
  RightItem,
  Schedule,
  UserOptions,
  Users,
} from './styles/StyledMenu';

import { Header } from '@wisecare-tech/design-system-web';
import { ReduxLogout } from '~/data/store/reducer/auth/usecases/ReduxLogout';
import { makeReduxGetAllNotificationUser } from '~/main/factories/usecases/Notifications/GetAllNotificationUserFactory';
import { makeReduxSetData } from '~/main/factories/usecases/appointment/SetDataFactory';
import { makeReduxActiveMessage } from '~/main/factories/usecases/message/Update';
import { makeSocketDisconnect } from '~/main/factories/usecases/waitingRoom/DisconnectSocketFactory';
import { History } from '~/main/routes';
import { translator } from '~/presentation/components/i18n';
import { closeModal } from '~/utils/closeModal';
import { menuOptions, userMenuOptions } from './menuOptions';
import { AvatarRoles } from '../avatar-roles';
import {
  Bell,
  ESusWhiteIcon,
  IconArrow,
  IconV4Hlogo,
} from '~/presentation/base/icons';
import { Popover } from '../UI/popover';
import { Icon } from '../icon';
import { Notification } from '../UI';

interface iTranslations {
  adm: string;
  users: string;
  clinicalDocs: string;
  appointment: string;
  teleShifts: string;
}

const Menu: React.FC = (): JSX.Element => {
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [translations, setTranslations] = useState<iTranslations>({
    adm: translator('Estatísticas') ?? 'Estatísticas',
    clinicalDocs: translator('Documentos') ?? 'Documentos',
    users: translator('Usuários') ?? 'Usuários',
    appointment: translator('Agenda') ?? 'Agenda',
    teleShifts: translator('Escala') ?? 'Escala',
  });

  const { auth, message } = useSelector((store: iStore) => store);
  const { role } = useSelector((store: iStore) => store.auth.selectUser);
  const refreshToken = useSelector((store: iStore) => store.auth.refresh.token);
  const results = useSelector((store: iStore) => store.selectedOrg.results);
  const { data } = message;
  const { state } = useLocation<{ from: string }>();

  useEffect(() => {
    if (results.length > 0 && results[0].org?.logo)
      setLogoUrl(results[0].org.logo);
  }, [results]);

  const handleLogout = useCallback(() => {
    const logoutApp = new ReduxLogout();
    logoutApp.logout();
    makeSocketDisconnect().disconnect();
    History.push('/');
  }, []);

  const changeRole = useCallback(() => {
    makeReduxActiveMessage().active({
      active: 'userChangeRole',
      actionCancel: closeModal,
    });
  }, []);

  const backToToday = useCallback(() => {
    makeReduxSetData().set({
      date: new Date().toISOString(),
    });
  }, []);

  const conference = (
    <div>
      <Link to="/appointment" onClick={backToToday}>
        <Drop
          resourcers={['APPOINTMENT']}
          actions={[
            'CREATE_OWN',
            'CREATE_ORG',
            'UPDATE_OWN',
            'UPDATE_ORG',
            'READ_OWN',
            'READ_ORG',
            'DELETE_OWN',
            'DELETE_ORG',
          ]}
        >
          {translator('Agenda de atendimentos')}
        </Drop>
      </Link>
      {role === 'PRO' && (
        <Link to="/appointment/me" onClick={backToToday}>
          <Drop
            resourcers={['APPOINTMENT']}
            actions={[
              'CREATE_OWN',
              'CREATE_ORG',
              'UPDATE_OWN',
              'UPDATE_ORG',
              'READ_OWN',
              'READ_ORG',
              'DELETE_OWN',
              'DELETE_ORG',
            ]}
          >
            {translator('Agenda do dia')}
          </Drop>
        </Link>
      )}
    </div>
  );

  const userOptions = (
    <UserOptions>
      <button
        type="button"
        onClick={() => {
          const url = window.location.pathname.replace(/\d+/g, '');

          if (url !== '/profile') {
            History.getHistory().push('/profile', {
              from: url.replaceAll('/', ''),
            });
          } else {
            History.getHistory().replace('/profile', {
              from: state.from,
            });
          }
        }}
      >
        <Drop id="btn_login">{`${translator('Perfil')}`}</Drop>
      </button>
      <button id="btn_org" type="button" onClick={() => changeRole()}>
        <Drop>{`${translator('Organizações')}`}</Drop>
      </button>
      <Drop id="btn_logout" onClick={() => handleLogout()}>
        {`${translator('Sair')}`}
      </Drop>
    </UserOptions>
  );

  const user = (
    <div>
      <Link to="/participant">
        <Drop resourcers={['PATIENT']} actions={['READ_ANY', 'READ_ORG']}>
          {translator('Pacientes')}
        </Drop>
      </Link>
      <Link to="/professional">
        <Drop
          resourcers={['ORGANIZER']}
          actions={[
            'READ_OWN',
            'READ_ORG',
            'READ_UNIT',
            'UPDATE_OWN',
            'DELETE_OWN',
          ]}
        >
          {translator('Profissionais de saúde')}
        </Drop>
      </Link>
      <Link to="/manager">
        <Drop
          resourcers={['ORGANIZER']}
          actions={[
            'CREATE',
            'CREATE_ORG',
            'UPDATE_ORG',
            'UPDATE_ANY',
            'READ_ORG',
            'READ_ANY',
            'DELETE_ORG',
            'DELETE_ANY',
          ]}
        >
          {translator('Organizadores')}
        </Drop>
      </Link>
      <Link to="/administrator">
        <Drop
          resourcers={['ADMINISTRATOR']}
          actions={['READ_OWN', 'READ_ANY', 'READ_ORG']}
        >
          {translator('Administradores')}
        </Drop>
      </Link>
    </div>
  );

  const adm = (
    <div>
      {/* Billing (serviços e faturamento) */}
      <Link to="/administrator/billing">
        <Drop
          resourcers={['ADMINISTRATOR']}
          actions={['READ_OWN', 'READ_ANY', 'READ_ORG']}
        >
          {translator('Serviços e faturamentos')}
        </Drop>
      </Link>
      {/* <Drop resourcers={['ADMINISTRATOR']} actions={['READ_OWN', 'READ_ANY']}>
        {translator('Painéis')}
      </Drop> */}
      <Link to="/administrator/audit">
        <Drop
          resourcers={['ADMINISTRATOR']}
          actions={['READ_OWN', 'READ_ANY', 'READ_ORG']}
        >
          {translator('Registro de ações')}
        </Drop>
      </Link>

      <Link to="/administrator/sectors">
        <Drop
          resourcers={['ADMINISTRATOR']}
          actions={['READ_OWN', 'READ_ANY', 'READ_ORG']}
        >
          {translator('Setores')}
        </Drop>
      </Link>
    </div>
  );

  const duty = (
    <div>
      {role !== 'ADM' && (
        <>
          <Link to="/duty/create">
            <Drop
              resourcers={['ONCALL']}
              actions={['REQUEST', 'READ_ORG', 'READ_ANY']}
            >
              {translator('Solicitar interconsulta')}
            </Drop>
          </Link>
          <Link to="/duty">
            <Drop
              resourcers={['ONCALL']}
              actions={['PROVIDE', 'READ_ORG', 'READ_ANY']}
            >
              {translator('Entrar no plantão')}
            </Drop>
          </Link>
        </>
      )}
    </div>
  );

  function callNotification() {
    makeReduxGetAllNotificationUser().getAll({
      filters: {
        channel: 'WEB',
        user: auth.info.user?.id,
      },
    });
  }

  return (
    // TODO: Atualizar Header para receber onClick além do href (caso necessário para userOptions atualmente)
    // <Header
    //   menuOptions={menuOptions({ role: auth.selectUser.role })}
    //   userOptions={userMenuOptions({ role: auth.selectUser.role })}
    //   user={{
    //     name: auth.info.user?.firstName || '',
    //     role: auth.selectUser.role,
    //     src: auth.info.user?.avatar,
    //   }}
    // />

    <Container>
      <div id="logo">
        {logoUrl ? (
          <Image alt="Logo" src={logoUrl} width="80px" height="46px" />
        ) : (
          <Icon src={IconV4Hlogo} width="100%" height="100%" />
        )}
      </div>
      <LeftItem>
        {/* AGENDA */}
        {role === 'PRO' ? (
          <Popover
            content={conference}
            originX="center"
            originY="bottom"
            resourcers={['APPOINTMENT']}
            actions={[
              'CREATE_OWN',
              'CREATE_ORG',
              'UPDATE_OWN',
              'UPDATE_ORG',
              'READ_OWN',
              'READ_ORG',
              'DELETE_OWN',
              'DELETE_ORG',
            ]}
          >
            <Schedule id="menu_appointment" icon>
              {translations.appointment}
              <IconArrow />
            </Schedule>
          </Popover>
        ) : (
          <Link to="/appointment">
            <Schedule
              resourcers={['APPOINTMENT']}
              actions={[
                'CREATE_OWN',
                'CREATE_ORG',
                'UPDATE_OWN',
                'UPDATE_ORG',
                'READ_OWN',
                'READ_ORG',
                'DELETE_OWN',
                'DELETE_ORG',
              ]}
            >
              {translator('Agenda')}
            </Schedule>
          </Link>
        )}

        {role !== 'ORG' && ( // TODO: Verificar ACL para caso necessário, renderizar pagina
          // DOCUMENTOS CLINICOS
          <Link to="/clinicaldocs">
            <ClinicalDocs
              resourcers={['CLINICAL_DOC']}
              actions={[
                'CREATE_OWN',
                'DELETE_OWN',
                'DELETE_ORG',
                'UPDATE_OWN',
                'READ_OWN',
                'READ_ORG',
                'SHARE',
              ]}
            >
              {translations.clinicalDocs}
            </ClinicalDocs>
          </Link>
        )}
        {role === 'CON' ? (
          ''
        ) : (
          // USUÁRIOS
          <Popover
            content={user}
            originX="center"
            originY="bottom"
            resourcers={[
              'PROFESSIONAL',
              'ORGANIZER',
              'ADMINISTRATOR',
              'PATIENT',
            ]}
            actions={['READ_ORG', 'READ_ANY', 'READ_OWN']}
          >
            <Users id="menu_users" icon>
              {translations.users}
              <IconArrow />
            </Users>
          </Popover>
        )}
        {/* ADMINISTRADORES */}
        <Popover
          content={adm}
          originX="center"
          originY="bottom"
          resourcers={['ADMINISTRATOR']}
          actions={['READ_OWN', 'READ_ANY', 'READ_ORG']}
        >
          <Adm icon>
            {translations.adm}
            <IconArrow />
          </Adm>
        </Popover>
        <Link to="/history">
          <AppointmentHistory>Histórico</AppointmentHistory>
        </Link>
        <Link to="/diagnosis">
          <AppointmentHistory>TeleLaudo</AppointmentHistory>
        </Link>
        {role !== 'CON' && (
          <Link to="/teleShifts">
            <AppointmentHistory>{translations.teleShifts}</AppointmentHistory>
          </Link>
        )}
        {role !== 'ADM' && (
          <Popover
            content={duty}
            resourcers={['ONCALL']}
            actions={[
              'READ_ORG',
              'READ_ANY',
              'READ_SHIFT_ORG',
              'READ_SHIFT_ANY',
            ]}
            originX="center"
            originY="bottom"
          >
            <Duty icon>
              <span>{translator('Plantão')}</span>
              <IconArrow />
            </Duty>
          </Popover>
        )}
      </LeftItem>
      <RightItem>
        <ESusWhiteIcon
          onClick={() =>
            window.open(
              'https://pecestadual.codata.pb.gov.br',
              'popup',
              'width=600,height=600',
            )
          }
        />

        <Popover originX="right" originY="bottom" content={<Notification />}>
          <Bell />
        </Popover>
        {/* <HelpPopover /> */}
        <Popover content={userOptions} originX="right" originY="bottom">
          <AvatarRoles
            name={`${auth?.info?.user?.firstName || ''} ${
              auth?.info?.user?.lastName || ''
            }`}
            role="organizer"
            cargo={role || 'ADM'}
            size="mini"
            src={auth.info.user?.avatar}
          />
        </Popover>
      </RightItem>
    </Container>
  );
};

export default Menu;
